/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ClipboardCheck, CircleDot, Wind, Target, MessageCircleHeart, ArrowRight, ScrollText, FileText } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [whakatauki, setWhakatauki] = useState(null);
  const [latestCheckin, setLatestCheckin] = useState(null);
  const [latestWheel, setLatestWheel] = useState(null);
  const [openGoals, setOpenGoals] = useState(0);

  useEffect(() => {
    api.get("/whakatauki/today").then((r) => setWhakatauki(r.data)).catch(() => {});
    api.get("/checkins?limit=1").then((r) => setLatestCheckin(r.data?.[0] || null)).catch(() => {});
    api.get("/wheel/latest").then((r) => setLatestWheel(r.data || null)).catch(() => {});
    api.get("/goals").then((r) => setOpenGoals((r.data || []).filter((g) => !g.completed).length)).catch(() => {});
  }, []);

  const today = new Date();
  const greeting = greetingFor(today);

  return (
    <div className="space-y-8 animate-fadeup">
      {/* Whakataukī */}
      <section className="bg-muka rounded-3xl p-7 sm:p-10 border border-border shadow-[0_4px_20px_rgba(42,42,42,0.05)]">
        <div className="text-[11px] uppercase tracking-[0.22em] text-kokowai mb-4">Whakataukī o te rā</div>
        {whakatauki ? (
          <>
            <p className="font-serif text-2xl sm:text-3xl text-charcoal italic leading-snug mb-3" data-testid="whakatauki-text">
              "{whakatauki.text}"
            </p>
            <p className="text-sm text-muted-foreground" data-testid="whakatauki-translation">
              {whakatauki.translation}
            </p>
          </>
        ) : (
          <div className="h-20 animate-pulse bg-bone rounded" />
        )}
      </section>

      {/* Greeting */}
      <section>
        <div className="text-[11px] uppercase tracking-[0.22em] text-kokowai mb-2">Kia ora</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-charcoal mb-2">
          {greeting}, {user?.display_name?.split(" ")[0]}.
        </h1>
        <p className="text-charcoal/70">
          {user?.intentions?.length
            ? `You said you're walking towards: ${user.intentions.slice(0, 2).join(" · ")}.`
            : "Take this slow today. Nothing has to be done."}
        </p>
      </section>

      {/* Quick actions grid */}
      <section className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <ActionCard
          to="/checkin"
          testid="action-checkin"
          eyebrow={latestCheckin ? "Last check-in: today" : "Today"}
          title="Te Whare Tapa Whā"
          body="Four walls. Notice each one. 90 seconds."
          icon={ClipboardCheck}
        />
        <ActionCard
          to="/wheel"
          testid="action-wheel"
          eyebrow={latestWheel ? "Last turn of the wheel" : "Map your reality"}
          title="The Reintegration Wheel"
          body="Seven domains. Where is the imbalance living?"
          icon={CircleDot}
        />
        <ActionCard
          to="/surveys"
          testid="action-surveys"
          eyebrow="Four domains, six questions each"
          title="Domain inventories"
          body="Housing · whānau · reintegration · addiction. Mirrors, not tests."
          icon={ScrollText}
        />
        <ActionCard
          to="/companion"
          testid="action-companion"
          eyebrow="Your sister · your kuia"
          title="Kōrero with Tuahine or Whaea"
          body="Pick the voice you need today."
          icon={MessageCircleHeart}
        />
        <ActionCard
          to="/toolkit"
          testid="action-toolkit"
          eyebrow="In the moment"
          title="Hā · breathing & grounding"
          body="When the wave hits — tools that fit in your hand."
          icon={Wind}
        />
        <ActionCard
          to="/goals"
          testid="action-goals"
          eyebrow={openGoals > 0 ? `${openGoals} open` : "Whāinga"}
          title="Goals & reminders"
          body="Probation, meds, mahi, parenting — your way."
          icon={Target}
        />
        <ActionCard
          to="/report"
          testid="action-report"
          eyebrow="On your device only"
          title="Pukapuka · printable report"
          body="Print or save as PDF. Your wheel, your inventories, your truth — to share or keep."
          icon={FileText}
        />
        <div className="bg-ngahere text-muka rounded-2xl p-6 sm:p-7 flex flex-col justify-between min-h-[180px]">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] opacity-70 mb-3">Reminder</div>
            <p className="font-serif text-xl leading-snug">
              You don't have to perform reintegration. You just have to keep showing up — to yourself.
            </p>
          </div>
          <div className="text-xs opacity-70 mt-4">— Whaea</div>
        </div>
      </section>

      {/* Today snapshot */}
      {latestCheckin && (
        <section className="bg-muka rounded-2xl p-6 sm:p-7 border border-border">
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-3">Latest check-in</div>
          <div className="grid grid-cols-4 gap-3">
            {[
              { k: "tinana", label: "Tinana", val: latestCheckin.tinana },
              { k: "hinengaro", label: "Hinengaro", val: latestCheckin.hinengaro },
              { k: "wairua", label: "Wairua", val: latestCheckin.wairua },
              { k: "whanau", label: "Whānau", val: latestCheckin.whanau },
            ].map((w) => (
              <div key={w.k} className="text-center">
                <div className="font-serif text-3xl text-ngahere">{w.val}</div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-1">
                  {w.label}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function ActionCard({ to, testid, eyebrow, title, body, icon: Icon }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group bg-muka rounded-2xl p-6 sm:p-7 border border-border hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(42,42,42,0.08)] transition-all duration-300 flex flex-col justify-between min-h-[180px]"
    >
      <div>
        <Icon size={22} strokeWidth={1.4} className="text-kokowai mb-3" />
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">{eyebrow}</div>
        <h3 className="font-serif text-xl text-charcoal leading-snug mb-1">{title}</h3>
        <p className="text-sm text-charcoal/70 leading-relaxed">{body}</p>
      </div>
      <ArrowRight size={16} className="text-ngahere mt-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
    </Link>
  );
}

function greetingFor(d) {
  const h = d.getHours();
  if (h < 5) return "Mahi i te pō";
  if (h < 12) return "Ata mārie";
  if (h < 17) return "Kia ora";
  if (h < 21) return "Ahiahi mārie";
  return "Pō mārie";
}